if (!customElements.get("custom-frequency")) {
  customElements.define(
    "custom-frequency",
    class CustomFrequency extends HTMLElement {
      connectedCallback() {
        // Native selling plan groups, Liquid-rendered from
        // product.selling_plan_groups. Shape:
        //   { groupId: { id, name, plans: [{ id, name, frequency, position, discounts }] } }
        this.plansMap = this.parseJSON(".cf-data-plans") || {};
        // Native selling plan allocations per variant, price already discounted
        // by Shopify. Shape: { variantId: { planId: { price, compare_at_price } } }
        this.sellingPlanPrices = this.parseJSON(".cf-data-selling-plans") || {};
        this.variantId = parseInt(this.dataset.variantId, 10);
        this.variantPrice = parseInt(this.dataset.variantPrice, 10);
        this.variantComparePrice = parseInt(this.dataset.variantComparePrice, 10) || 0;
        this.variantTitle = this.dataset.variantTitle || "";
        this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
        this.currencyCode = this.dataset.currencyCode || "AED";
        this.formId = this.dataset.productFormId;
        this.sellingPlanInput = this.querySelector("[data-cf-selling-plan]");
        this.root = this.querySelector("[data-cf-root]");

        this.sticksPerPack = parseInt(this.dataset.sticksPerPack, 10) || 0;
        this.stickLabel = this.dataset.stickLabel || "stick";
        this.stickLabelPlural = this.dataset.stickLabelPlural || "";
        this.cfOneSubtitle = this.dataset.cfOneSubtitle || "";
        this.bundleQty = 1;
        this.bundleDiscount = null;

        this.selectedPlanId = null;
        this.mode = null;

        this.customSubscribeDiscount = {
          type: this.dataset.subscribeDiscountType,
          value: Number(this.dataset.subscribeDiscountValue) || 0
        };

        this.customOnetimeDiscount = {
          type: this.dataset.onetimeDiscountType,
          value: Number(this.dataset.onetimeDiscountValue) || 0
        };

        this.render();
        this.subscribeVariantChange();
        this.subscribeBundleChange();
      }

      disconnectedCallback() {
        if (typeof this.unsubscribe === "function") this.unsubscribe();
        if (this.onBundleChange) {
          document.removeEventListener("bundle:quantity-change", this.onBundleChange);
        }
      }

      parseJSON(selector) {
        const el = this.querySelector(selector);
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (_) {
          return null;
        }
      }

      // ── data pipeline ──────────────────────────────────────────────────────
      // plansMap comes from .cf-data-plans (Liquid-rendered from
      // product.selling_plan_groups). A plan only applies to a variant when
      // that variant has a selling plan allocation for it, so plans are
      // filtered against this.sellingPlanPrices[variantId].
      allocationsForVariant() {
        return (this.sellingPlanPrices || {})[String(this.variantId)] || {};
      }

      // Map a native delivery frequency (days) to a friendly chip label.
      frequencyParts(freqDays) {
        const freq = Number(freqDays) || 0;
        if (freq >= 365 && freq % 365 === 0) return { num: freq / 365, unit: "year" };
        if (freq >= 30 && freq % 30 === 0) return { num: freq / 30, unit: "month" };
        if (freq >= 7 && freq % 7 === 0) return { num: freq / 7, unit: "week" };
        return { num: freq || 1, unit: "day" };
      }

      // Some plans (e.g. created by third-party apps) leave
      // recurring_delivery_frequency empty and only carry the frequency in
      // their options (e.g. "Delivery Frequency" → "1 month"). Parse that.
      frequencyFromOptions(options) {
        const list = options || [];
        const opt =
          list.find((o) => /freq|deliver|every|interval/i.test(String(o.name || ""))) ||
          list.find((o) => /day|week|month|year|daily|weekly|monthly|annual/i.test(String(o.value || "")));
        if (!opt) return null;
        const v = String(opt.value || opt.name || "");
        const m = v.match(/(\d+)\s*(day|week|month|year)s?/i);
        if (m) {
          return { num: parseInt(m[1], 10), unit: m[2].toLowerCase() };
        }
        const w = v.match(/(daily|weekly|monthly|annually|yearly)/i);
        if (w) {
          const map = { daily: [1, "day"], weekly: [1, "week"], monthly: [1, "month"], annually: [1, "year"], yearly: [1, "year"] };
          const pair = map[w[1].toLowerCase()];
          return { num: pair[0], unit: pair[1] };
        }
        return null;
      }

      // Normalize a native plan into the shape the render pipeline expects.
      // sellingPlanId stays the numeric Shopify id; the submit path splits on
      // "/" for GIDs, which leaves plain numbers untouched.
      normalizePlan(p) {
        const id = String(p.id);
        const discounts = (p.discounts || [])
          .map((d) => {
            let type = "fixed";
            let value = Number(d.value);
            if (d.type === "percentage") {
              type = "percentage";
            } else if (d.type === "price") {
              // Fixed price per delivery → discount relative to variant price.
              value = Math.max(0, (this.variantPrice - value)) / 100;
            }
            return { fromOrder: Number(d.position) || 1, type, value };
          })
          .sort((a, b) => a.fromOrder - b.fromOrder);
        const parts =
          Number(p.frequency) > 0
            ? this.frequencyParts(p.frequency)
            : this.frequencyFromOptions(p.options) || { num: 1, unit: "day" };
        return Object.assign({}, p, {
          identify: id,
          sellingPlanId: id,
          position: Number(p.position) || 0,
          frequencyValue: parts.num,
          frequency: parts.unit,
          enabledDiscount: discounts.length > 0,
          discountConfig: { enabled: discounts.length > 0, tiers: discounts },
        });
      }

      buildState() {
        // Selling plan IDs reserved for BYO page only — hidden on product detail.
        const BYO_ONLY_PLAN_IDS = ['5117673723'];
        const allocs = this.allocationsForVariant();
        const groups = Object.entries(this.plansMap || {})
          .map(([id, g]) => Object.assign({ id }, g || {}))
          .map((g) =>
            Object.assign(g, {
              plans: (g.plans || [])
                .map((p) => this.normalizePlan(p))
                .filter((p) => allocs[String(p.id)])
                .filter((p) => {
                  const pid = String(p.sellingPlanId || p.id || "").split("/").pop();
                  return !BYO_ONLY_PLAN_IDS.includes(pid);
                })
                .sort((a, b) => (a.position || 0) - (b.position || 0)),
            }),
          )
          .filter((g) => g.plans.length);

        if (!groups.length) return { lead: null, plans: [] };

        // Native groups have no priority ordering — keep the admin's array order.
        const lead = groups[0];
        return { lead, plans: lead.plans };
      }

      // Initial discount = tier covering order 1; recurring = last tier.
      planDiscount(plan) {
        if (!plan || !plan.enabledDiscount)
          return { initial: null, recurring: null };
        const tiers =
          plan.discountConfig && plan.discountConfig.enabled
            ? plan.discountConfig.tiers || []
            : [];
        if (!tiers.length) {
          if (plan.discountValue == null)
            return { initial: null, recurring: null };
          const fallback = {
            type: plan.discountType || "percentage",
            value: Number(plan.discountValue),
          };
          return { initial: fallback, recurring: fallback };
        }
        const sorted = tiers
          .slice()
          .sort(
            (a, b) => (Number(a.fromOrder) || 0) - (Number(b.fromOrder) || 0),
          );
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        return {
          initial: { type: first.type, value: Number(first.value) },
          recurring: { type: last.type, value: Number(last.value) },
        };
      }

      formatDiscount(d) {
        if (!d || d.value == null || isNaN(d.value)) return "";
        if (d.type === "percentage") return `${d.value}%`;
        if (d.type === "fixed")
          return this.formatMoney(Math.round(d.value * 100));
        return String(d.value);
      }

      // Returns a "Save X%" label combining bundle + subscription discounts.
      stackedSaveLabel(bundleDiscount, planDiscount) {
        const bv = bundleDiscount && bundleDiscount.type === 'percentage' ? (bundleDiscount.value || 0) : 0;
        const pv = planDiscount  && planDiscount.type  === 'percentage' ? (planDiscount.value  || 0) : 0;
        if (!bv && !pv) return '';
        const combined = Math.round((100 - (1 - bv / 100) * (1 - pv / 100) * 100) * 10) / 10;
        const display  = combined % 1 === 0 ? combined.toFixed(0) : String(combined);
        return display + '%';
      }

      applyDiscountToPrice(priceCents, discount) {
        if (!discount || discount.value == null) return priceCents;
        if (discount.type === "percentage") {
          return Math.round(priceCents * (1 - discount.value / 100));
        }
        if (discount.type === "fixed") {
          return Math.max(0, priceCents - Math.round(discount.value * 100));
        }
        return priceCents;
      }

      // ── render ─────────────────────────────────────────────────────────────
      render() {
        const { lead, plans } = this.buildState();

        if (!lead || !plans.length) {
          this.hidden = true;
          this.clearSellingPlan();
          return;
        }
        this.hidden = false;

        // Native plans have no default flag — preselect the first plan.
        const selected = plans[0];

        this.selectedPlanId = selected.identify || selected.id;

        const showOneTime = this.dataset.oneTimeShow !== "false";
        // Default to one-time; keep the shopper's choice across re-renders
        // (variant / bundle changes). Fall back to subscribe when one-time is hidden.
        if (!showOneTime) this.mode = "subscribe";
        else if (!this.mode) this.mode = "onetime";
        // Always show compare price when available.
        const showCompareAt = true;

        const subscribeRow = this.renderSubscribeRow(
          selected,
          plans,
          showCompareAt,
        );
        const onetimeRow = showOneTime ? this.renderOneTimeRow() : "";

        this.root.innerHTML = subscribeRow + onetimeRow;

        this.attachEvents();
        this.syncSellingPlan();
        this.broadcastPrice();
      }

      buildDescriptionLines() {
        const raw = this.dataset.subscribeDescriptionLines || "";
        return raw
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);
      }

      renderDescriptionList(lines) {
        const icon =
          '<svg class="cf-desc__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<path fill-rule="evenodd" clip-rule="evenodd" d="M8 2.5C4.96219 2.5 2.5 4.96219 2.5 8C2.5 11.0372 4.96223 13.5 8 13.5C11.0378 13.5 13.5 11.0372 13.5 8C13.5 4.96219 11.0378 2.5 8 2.5ZM1.5 8C1.5 4.40991 4.40991 1.5 8 1.5C11.5901 1.5 14.5 4.40991 14.5 8C14.5 11.5894 11.5901 14.5 8 14.5C4.40987 14.5 1.5 11.5894 1.5 8Z" fill="currentColor"/>' +
          '<path fill-rule="evenodd" clip-rule="evenodd" d="M10.6634 6.1069C10.8586 6.30216 10.8586 6.61874 10.6634 6.81397L7.58484 9.8925C7.38964 10.0877 7.0731 10.0878 6.87784 9.89257L5.33793 8.35331C5.14262 8.15811 5.14256 7.84151 5.33778 7.64624C5.533 7.45091 5.84958 7.45084 6.04488 7.64604L7.23124 8.83191L9.95624 6.1069C10.1515 5.91163 10.4681 5.91163 10.6634 6.1069Z" fill="currentColor"/>' +
          '</svg>';
        return lines.map((d) => `<li>${icon}${this.escape(d)}</li>`).join('');
      }

      bundleSubtotal() {
        const qty = this.bundleQty || 1;
        return this.variantPrice * qty;
      }

      stickSubtitle() {
        if (this.cfOneSubtitle) {
          return this.cfOneSubtitle;
        }
        if (!this.sticksPerPack) return this.variantTitle || "";
        const total = (this.bundleQty || 1) * this.sticksPerPack;
        let label;
        if (total > 1) {
          label = this.stickLabelPlural || (this.stickLabel + "s");
        } else {
          label = this.stickLabel;
        }
        return total + " " + label;
      }

      // "AED 5.63 per stick" — price split across all sticks in the bundle.
      perUnitText(priceCents) {
        if (!this.sticksPerPack || !priceCents) return "";
        const total = (this.bundleQty || 1) * this.sticksPerPack;
        return this.formatMoney(Math.round(priceCents / total)) + " per " + this.stickLabel;
      }

      // Returns the best compare price (cents) to show as strikethrough:
      // - Prefers Shopify's compare_at_price × bundleQty when configured (original price before sale)
      // - Falls back to the un-discounted variant subtotal (variantPrice × bundleQty)
      comparePrice() {
        const qty = this.bundleQty || 1;
        if (this.variantComparePrice > 0) {
          return this.variantComparePrice * qty;
        }
        return this.bundleSubtotal();
      }

      // Returns the Shopify-native discounted price (cents) for the given
      // selling plan ID, scaled by bundleQty. Returns null when not available.
      getNativeSellingPlanPrice(sellingPlanId) {
        if (!sellingPlanId) return null;
        const id = String(sellingPlanId).split('/').pop();
        const entry = this.allocationsForVariant()[id];
        if (!entry || entry.price == null) return null;
        const qty = this.bundleQty || 1;
        return entry.price * qty;
      }

      renderSubscribeRow(selected, plans, showCompareAt) {
        const sectionId = this.dataset.sectionId;
        //const { initial } = this.planDiscount(selected);

        let { initial } = this.planDiscount(selected);

        if (this.customSubscribeDiscount.value > 0) {
          initial = this.customSubscribeDiscount;
        }

        const subtotal = this.bundleSubtotal();
        // Prefer Shopify native price (already discounted) when available;
        // fall back to the plan's native price adjustment discount.
        const sellingPlanId = selected && (selected.sellingPlanId || selected.identify || selected.id);
        const nativePrice = this.getNativeSellingPlanPrice(sellingPlanId);
        

        let subscribePrice;

        if (this.customSubscribeDiscount?.value > 0) {
          subscribePrice = this.applyDiscountToPrice(
            subtotal,
            this.customSubscribeDiscount
          );
        } else {
          const afterBundle = this.applyDiscountToPrice(
            subtotal,
            this.bundleDiscount
          );

           subscribePrice = nativePrice != null
            ? nativePrice
            : this.applyDiscountToPrice(afterBundle, initial);
        }



       

       
        //const saveLabel = this.stackedSaveLabel(this.bundleDiscount, initial);

        let saveLabel;

        if (this.customSubscribeDiscount?.value > 0) {
          saveLabel = this.formatDiscount(
            this.customSubscribeDiscount
          );
        } else {
          saveLabel = this.stackedSaveLabel(
            this.bundleDiscount,
            initial
          );
        }

        // Use Shopify compare_at_price when available; otherwise fall back to
        // the un-discounted subtotal so the strikethrough reflects the true
        // original price the customer saved on.
        const compareAmt = this.comparePrice();
        const descLines = this.buildDescriptionLines();

        const showBestValue = this.dataset.bestValueShow === "true";
        const bestValueText = this.dataset.bestValueText || "Best Value";
        const bestValueBadge = showBestValue
          ? `<div class="cf-best-value" aria-hidden="true">
              <svg class="cf-best-value__icon" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.6438 6.64875L13.5038 5.50875C13.3088 5.31375 13.1513 4.93125 13.1513 4.66125V3.04125C13.1513 2.38125 12.6113 1.84125 11.9513 1.84125H10.3387C10.0687 1.84125 9.68625 1.68375 9.49125 1.48875L8.35125 0.34875C7.88625 -0.11625 7.12125 -0.11625 6.65625 0.34875L5.50125 1.48875C5.31375 1.68375 4.93125 1.84125 4.65375 1.84125H3.04125C2.38125 1.84125 1.84125 2.38125 1.84125 3.04125V4.65375C1.84125 4.92375 1.68375 5.30625 1.48875 5.50125L0.34875 6.64125C-0.11625 7.10625 -0.11625 7.87125 0.34875 8.33625L1.48875 9.47625C1.68375 9.67125 1.84125 10.0538 1.84125 10.3238V11.9362C1.84125 12.5962 2.38125 13.1363 3.04125 13.1363H4.65375C4.92375 13.1363 5.30625 13.2938 5.50125 13.4888L6.64125 14.6288C7.10625 15.0938 7.87125 15.0938 8.33625 14.6288L9.47625 13.4888C9.67125 13.2938 10.0538 13.1363 10.3238 13.1363H11.9362C12.5962 13.1363 13.1363 12.5962 13.1363 11.9362V10.3238C13.1363 10.0538 13.2938 9.67125 13.4888 9.47625L14.6288 8.33625C15.1163 7.87875 15.1163 7.11375 14.6438 6.64875ZM4.49625 5.24625C4.49625 4.83375 4.83375 4.49625 5.24625 4.49625C5.65875 4.49625 5.99625 4.83375 5.99625 5.24625C5.99625 5.65875 5.66625 5.99625 5.24625 5.99625C4.83375 5.99625 4.49625 5.65875 4.49625 5.24625ZM5.64375 10.1437C5.53125 10.2562 5.38875 10.3087 5.24625 10.3087C5.10375 10.3087 4.96125 10.2562 4.84875 10.1437C4.63125 9.92625 4.63125 9.56625 4.84875 9.34875L9.34875 4.84875C9.56625 4.63125 9.92625 4.63125 10.1437 4.84875C10.3612 5.06625 10.3612 5.42625 10.1437 5.64375L5.64375 10.1437ZM9.74625 10.4962C9.32625 10.4962 8.98875 10.1587 8.98875 9.74625C8.98875 9.33375 9.32625 8.99625 9.73875 8.99625C10.1512 8.99625 10.4887 9.33375 10.4887 9.74625C10.4887 10.1587 10.1587 10.4962 9.74625 10.4962Z" fill="currentColor"/></svg>
              <span class="cf-best-value__text">${this.escape(bestValueText)}</span>
            </div>`
          : "";

        return `
          <div class="cf-row cf-row--subscribe ${this.mode === "subscribe" ? "is-active" : ""} ${showBestValue ? "cf-row--has-best-value" : ""}" data-cf-row="subscribe">            
            <label class="cf-radio">
              <input type="radio" class="cf-radio-input" name="cf-mode-${sectionId}" value="subscribe" ${this.mode === "subscribe" ? "checked" : ""}>
              <span class="cf-radio__circle"></span>
            </label>
            <div class="cf-content">
              <div class="cf-content__head">
                <div class="cf-heading">
                  <div class="cf-title">
                    <span>Subscribe &amp;</span>
                    ${saveLabel ? `<span class="cf-save" data-cf-save>Save ${this.escape(saveLabel)}</span>` : `<span class="cf-save" data-cf-save></span>`}
                  </div>
                    ${bestValueBadge}
                </div>
                <div class="cf-prices">
                  <div class="cf-prices__main">
                    <span class="cf-prices__current" data-cf-sub-price>${this.renderPriceHtml(subscribePrice)}</span>
                    ${
                      showCompareAt && compareAmt > subscribePrice
                        ? `<s class="cf-prices__compare" data-cf-sub-compare>${this.renderPriceHtml(compareAmt)}</s>`
                        : `<s class="cf-prices__compare" data-cf-sub-compare hidden></s>`
                    }
                  </div>
                  <span class="cf-prices__per-unit" data-cf-sub-per-unit>${this.escape(this.perUnitText(subscribePrice))}</span>
                </div>
              </div>
              <div class="cf-desc-wrap">
                <ul class="cf-desc" data-cf-desc>
                  ${this.renderDescriptionList(descLines)}
                </ul>
              </div>
              <div class="cf-chips-label">Delivers every</div>
              <div class="cf-chips" role="radiogroup" aria-label="Delivery frequency">
                ${plans
                  .map((p) => {
                    const id = p.identify || p.id;
                    const isSel = String(id) === String(this.selectedPlanId);
                    return `
                      <button
                        type="button"
                        class="cf-chip ${isSel ? "is-selected" : ""}"
                        role="radio"
                        aria-checked="${isSel ? "true" : "false"}"
                        data-cf-plan="${this.escape(id)}"
                      >
                        <span class="cf-chip__num">${p.frequencyValue}</span><span class="cf-chip__unit">${p.frequency}${p.frequencyValue === 1 ? "" : "s"}</span>
                        <svg class="cf-chip__badge" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.662 6.81467L7.58333 9.89333C7.48533 9.99133 7.358 10.04 7.23 10.04C7.10133 10.04 6.974 9.99133 6.876 9.89333L5.33667 8.354C5.14133 8.15867 5.14133 7.842 5.33667 7.64667C5.532 7.45133 5.848 7.45133 6.04333 7.64667L7.23 8.83267L9.95467 6.10733C10.15 5.912 10.4667 5.912 10.662 6.10733C10.8573 6.30267 10.8573 6.61933 10.662 6.81467ZM10.8113 1.66667H5.188C3.082 1.66667 1.66667 3.14733 1.66667 5.35133V10.65C1.66667 12.8533 3.082 14.3333 5.188 14.3333H10.8107C12.9173 14.3333 14.3333 12.8533 14.3333 10.65V5.35133C14.3333 3.14733 12.918 1.66667 10.8113 1.66667Z" fill="currentColor"/></svg>
                      </button>
                    `;
                  })
                  .join("")}
              </div>
            </div>
          </div>
        `;
      }

      renderOneTimeRow() {
        const sectionId = this.dataset.sectionId;
        const subtotal = this.bundleSubtotal();
        //const onetimePrice = this.applyDiscountToPrice(subtotal, this.bundleDiscount);

        let onetimePrice = this.applyDiscountToPrice(
          subtotal,
          this.bundleDiscount
        );

        if (this.customOnetimeDiscount.value > 0) {
          onetimePrice = this.applyDiscountToPrice(
            subtotal,
            this.customOnetimeDiscount
          );
        }

        // Compare price for one-time row: Shopify compare_at_price when set,
        // otherwise the un-discounted subtotal (shows savings from bundle discount).
        const compareAmt = this.comparePrice();
        const showOneCompare = compareAmt > onetimePrice;
        return `
          <div class="cf-row cf-row--onetime ${this.mode === "onetime" ? "is-active" : ""}" data-cf-row="onetime">
            <label class="cf-radio">
              <input type="radio" class="cf-radio-input" name="cf-mode-${sectionId}" value="onetime" ${this.mode === "onetime" ? "checked" : ""}>
              <span class="cf-radio__circle"></span>
            </label>
            <div class="cf-content cf-content--inline">
              <div class="cf-heading">
                <div class="cf-title">One time purchase</div>
                <div class="cf-subtitle" data-cf-one-subtitle>${this.escape(this.stickSubtitle())}</div>
                ${this.dataset.onetimeBlurbShow !== 'false' ? `<div class="cf-onetime-blurb">${this.escape(this.dataset.onetimeBlurbText || 'Try your first box. No commitment.')}</div>` : ''}
              </div>
              <div class="cf-prices">
                <span class="cf-prices__current" data-cf-one-price>${this.renderPriceHtml(onetimePrice)}</span>
                ${showOneCompare
                  ? `<s class="cf-prices__compare" data-cf-one-compare>${this.renderPriceHtml(compareAmt)}</s>`
                  : `<s class="cf-prices__compare" data-cf-one-compare hidden></s>`
                }
              </div>
            </div>
          </div>
        `;
      }

      renderPriceHtml(cents) {
        // Splits "AED 88.00" → "<small>AED</small><strong>88.00</strong>"
        const formatted = this.formatMoney(cents);
        // Find the leading currency code/symbol (anything before the first digit)
        const m = formatted.match(/^(\D+)([\d.,\s]+)$/);
        if (m) {
          return `<span class="cf-prices__currency">${this.escape(m[1].trim())}</span><span class="cf-prices__amount">${this.escape(m[2].trim())}</span>`;
        }
        // Fallback: trailing-code shop formats ("112.59AED") still render as
        // the storefront contract: "AED 112.59".
        const m2 = formatted.match(/^([\d.,\s]+)(\D+)$/);
        if (m2) {
          return `<span class="cf-prices__currency">${this.escape(m2[2].trim())}</span><span class="cf-prices__amount">${this.escape(m2[1].trim())}</span>`;
        }
        return this.escape(formatted);
      }

      // ── interaction ────────────────────────────────────────────────────────
      attachEvents() {
        this.querySelectorAll(".cf-chip").forEach((btn) => {
          btn.addEventListener("click", () => this.handleChipClick(btn));
        });
        this.querySelectorAll(
          `input[name="cf-mode-${this.dataset.sectionId}"]`,
        ).forEach((input) => {
          input.addEventListener("change", () =>
            this.handleModeChange(input.value),
          );
        });

        const subRow = this.querySelector('[data-cf-row="subscribe"]');
        const oneRow = this.querySelector('[data-cf-row="onetime"]');
        subRow &&
          subRow.addEventListener("click", (e) => {
            if (e.target.closest(".cf-chip")) return;
            this.setRadio("subscribe");
          });
        oneRow &&
          oneRow.addEventListener("click", () => this.setRadio("onetime"));
      }

      setRadio(mode) {
        const radio = this.querySelector(
          `input[name="cf-mode-${this.dataset.sectionId}"][value="${mode}"]`,
        );
        if (radio && !radio.checked) {
          radio.checked = true;
          this.handleModeChange(mode);
        }
      }

      handleChipClick(btn) {
        const planId = btn.dataset.cfPlan;
        if (!planId) return;
        this.selectedPlanId = planId;

        this.querySelectorAll(".cf-chip").forEach((b) => {
          const on = b.dataset.cfPlan === planId;
          b.classList.toggle("is-selected", on);
          b.setAttribute("aria-checked", on ? "true" : "false");
        });

        this.setRadio("subscribe");
        this.refreshForSelectedPlan();
        this.syncSellingPlan();
      }

      handleModeChange(mode) {
        this.mode = mode;
        const subRow = this.querySelector('[data-cf-row="subscribe"]');
        const oneRow = this.querySelector('[data-cf-row="onetime"]');
        subRow && subRow.classList.toggle("is-active", mode === "subscribe");
        oneRow && oneRow.classList.toggle("is-active", mode === "onetime");
        this.syncSellingPlan();
        this.broadcastPrice();
      }

      effectivePriceCents() {
        const subtotal = this.bundleSubtotal();
        if (this.mode !== "subscribe") {
          return Math.round(this.applyDiscountToPrice(subtotal, this.bundleDiscount));
        }
        const { plans } = this.buildState();
        const sel = plans.find(
          (p) => String(p.identify || p.id) === String(this.selectedPlanId),
        );
        if (!sel) return Math.round(subtotal);
        const sellingPlanId = sel.sellingPlanId || sel.identify || sel.id;
        const nativePrice = this.getNativeSellingPlanPrice(sellingPlanId);
        if (nativePrice != null) return Math.round(nativePrice);
        const { initial } = this.planDiscount(sel);
        //const afterBundle = this.applyDiscountToPrice(subtotal, this.bundleDiscount);
        //return Math.round(this.applyDiscountToPrice(afterBundle, initial));

        if (this.customSubscribeDiscount?.value > 0) {
          return Math.round(
            this.applyDiscountToPrice(
              subtotal,
              this.customSubscribeDiscount
            )
          );
        }
      }

      // Subscription price (cents) for the selected plan and bundle, whatever
      // mode is active. Same rules as the subscribe row in refreshForSelectedPlan.
      // Returns null when the product has no subscription plan.
      subscribePriceCents() {
        const { plans } = this.buildState();
        const sel = plans.find(
          (p) => String(p.identify || p.id) === String(this.selectedPlanId),
        );
        if (!sel) return null;

        const subtotal = this.bundleSubtotal();
        if (this.customSubscribeDiscount?.value > 0) {
          return Math.round(
            this.applyDiscountToPrice(subtotal, this.customSubscribeDiscount),
          );
        }

        const sellingPlanId = sel.sellingPlanId || sel.identify || sel.id;
        const nativePrice = this.getNativeSellingPlanPrice(sellingPlanId);
        if (nativePrice != null) return Math.round(nativePrice);

        const { initial } = this.planDiscount(sel);
        const afterBundle = this.applyDiscountToPrice(subtotal, this.bundleDiscount);
        return Math.round(this.applyDiscountToPrice(afterBundle, initial));
      }

      broadcastPrice() {
        const detail = {
          priceCents: this.effectivePriceCents(),
          subscribePriceCents: this.subscribePriceCents(),
          mode: this.mode,
          sectionId: this.dataset.sectionId || "",
        };
        document.dispatchEvent(new CustomEvent("cf:price-change", { detail: detail }));
      }

      refreshForSelectedPlan() {
        const { plans } = this.buildState();
        const sel = plans.find(
          (p) => String(p.identify || p.id) === String(this.selectedPlanId),
        );
        if (!sel) return;

        //const { initial } = this.planDiscount(sel);

        let { initial } = this.planDiscount(sel);

        if (this.customSubscribeDiscount.value > 0) {
          initial = this.customSubscribeDiscount;
        }

        const subtotal = this.bundleSubtotal();
        // Prefer Shopify native price; fall back to the plan's native adjustment.
        const sellingPlanId = sel.sellingPlanId || sel.identify || sel.id;
        const nativePrice = this.getNativeSellingPlanPrice(sellingPlanId);
       


          let newPrice;

          if (this.customSubscribeDiscount?.value > 0) {
            newPrice = this.applyDiscountToPrice(
              subtotal,
              this.customSubscribeDiscount
            );
          } else {
            const afterBundle2 = this.applyDiscountToPrice(
              subtotal,
              this.bundleDiscount
            );

            newPrice = nativePrice != null
              ? nativePrice
              : this.applyDiscountToPrice(afterBundle2, initial);
          }

        const cur = this.querySelector("[data-cf-sub-price]");
        const cmp = this.querySelector("[data-cf-sub-compare]");
        if (cur) cur.innerHTML = this.renderPriceHtml(newPrice);
        const perUnit = this.querySelector("[data-cf-sub-per-unit]");
        if (perUnit) perUnit.textContent = this.perUnitText(newPrice);
        if (cmp) {
          const compareAmt = this.comparePrice();
          if (compareAmt > newPrice) {
            cmp.innerHTML = this.renderPriceHtml(compareAmt);
            cmp.hidden = false;
          } else {
            cmp.innerHTML = "";
            cmp.hidden = true;
          }
        }

        const save = this.querySelector("[data-cf-save]");
        //const label = this.stackedSaveLabel(this.bundleDiscount, initial);

        let label;

        if (this.customSubscribeDiscount?.value > 0) {
          label = this.formatDiscount(
            this.customSubscribeDiscount
          );
        } else {
          label = this.stackedSaveLabel(
            this.bundleDiscount,
            initial
          );
        }

        if (save) {
          if (label) {
            save.textContent = `Save ${label}`;
            save.hidden = false;
          } else {
            save.hidden = true;
          }
        }

        const oneSubtitle = this.querySelector("[data-cf-one-subtitle]");
        if (oneSubtitle) oneSubtitle.textContent = this.stickSubtitle();

        const onePrice = this.querySelector("[data-cf-one-price]");
        const oneCmp = this.querySelector("[data-cf-one-compare]");
        //const onetimePriceVal = this.applyDiscountToPrice(subtotal, this.bundleDiscount);

        let onetimePriceVal = this.applyDiscountToPrice(
          subtotal,
          this.bundleDiscount
        );

        if (this.customOnetimeDiscount.value > 0) {
          onetimePriceVal = this.applyDiscountToPrice(
            subtotal,
            this.customOnetimeDiscount
          );
        }

        if (onePrice) {
          onePrice.innerHTML = this.renderPriceHtml(onetimePriceVal);
        }
        if (oneCmp) {
          const oneCompareAmt = this.comparePrice();
          if (oneCompareAmt > onetimePriceVal) {
            oneCmp.innerHTML = this.renderPriceHtml(oneCompareAmt);
            oneCmp.hidden = false;
          } else {
            oneCmp.innerHTML = "";
            oneCmp.hidden = true;
          }
        }

        const descUl = this.querySelector("[data-cf-desc]");
        if (descUl) {
          descUl.innerHTML = this.renderDescriptionList(
            this.buildDescriptionLines(),
          );
        }

        this.broadcastPrice();
      }

      subscribeBundleChange() {
        this.onBundleChange = (e) => {
          if (!e || !e.detail) return;
          // Scope to same section if provided
          if (
            e.detail.sectionId &&
            this.dataset.sectionId &&
            String(e.detail.sectionId) !== String(this.dataset.sectionId)
          ) {
            return;
          }
          const qty = parseInt(e.detail.qty, 10);
          if (!qty || qty < 1) return;
          this.bundleQty = qty;
          this.bundleDiscount = {
            type:
              e.detail.discountType === "fixed-amount" ||
              e.detail.discountType === "fixed"
                ? "fixed"
                : "percentage",
            value: Number(e.detail.discountValue) || 0,
          };
          this.refreshForSelectedPlan();
          // Discount codes are managed natively by Avada Bundle app.
        };
        document.addEventListener("bundle:quantity-change", this.onBundleChange);

        // Handle race condition: bundle widget may have already dispatched
        // bundle:quantity-change before this listener was registered (e.g. when
        // ux-bundle-sub.js runs synchronously and custom-frequency.js loads via
        // defer). Read the current bundle state directly from the DOM so compare
        // price is shown correctly on page load without requiring user interaction.
        this.syncInitialBundleState();
      }

      syncInitialBundleState() {
        // Look for the bundle selector widget scoped to the same section when
        // possible, falling back to the first one on the page.
        const sectionId = this.dataset.sectionId;
        let bundleWidget = sectionId
          ? document.querySelector(`[data-avada-bundle-quantity][data-section-id="${sectionId}"]`)
          : null;
        if (!bundleWidget) {
          bundleWidget = document.querySelector("[data-avada-bundle-quantity]");
        }
        if (!bundleWidget) return;

        const checkedRadio = bundleWidget.querySelector(".bundle-selector__radio:checked");
        if (!checkedRadio) return;

        const qty = parseInt(checkedRadio.value, 10);
        if (!qty || qty < 1) return;

        const discountValue = Number(checkedRadio.getAttribute("data-discount")) || 0;
        const discountType = checkedRadio.getAttribute("data-discount-type") || "percentage";

        this.bundleQty = qty;
        this.bundleDiscount = {
          type:
            discountType === "fixed-amount" || discountType === "fixed"
              ? "fixed"
              : "percentage",
          value: discountValue,
        };

        // Re-render with the correct bundle state so compare price is visible
        // immediately on page load.
        this.render();
      }

      // ── form sync ────────────────────────────────────────────
      syncSellingPlan() {
        if (!this.sellingPlanInput) return;
        if (this.mode === "subscribe") {
          const { plans } = this.buildState();
          const sel = plans.find(
            (p) => String(p.identify || p.id) === String(this.selectedPlanId),
          );
          const raw = sel && sel.sellingPlanId ? String(sel.sellingPlanId) : "";
          this.sellingPlanInput.value = raw ? raw.split("/").pop() : "";
        } else {
          this.sellingPlanInput.value = "";
        }
      }

      clearSellingPlan() {
        if (this.sellingPlanInput) this.sellingPlanInput.value = "";
      }

      // ── utilities ──────────────────────────────────────────────────────────
      formatMoney(cents) {
        let res;
        if (
          window.Shopify &&
          typeof window.Shopify.formatMoney === "function"
        ) {
          res = window.Shopify.formatMoney(cents, this.moneyFormat);
        } else {
          const amount = (cents / 100).toFixed(2);
          const noDecimals = String(Math.round(cents / 100));
          const withComma = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          res = this.moneyFormat
            .replace(/\{\{\s*amount\s*\}\}/g, amount)
            .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
            .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
            .replace(
              /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
              noDecimals,
            );
        }
        // Prices are presentment-currency cents (product.price in the active
        // market currency). The money format string is the shop's base-currency
        // format ("Dhs {{amount}}" / "AED {{amount}}"), so force the active
        // market currency code as the label — e.g. "AED 88.00" -> "USD 88.00".
        if (res) {
          const code = this.currencyCode || "AED";
          const lead = res.match(/^(\D+)([\d.,\s]+)$/);
          if (lead) {
            res = code + " " + lead[2].trim();
          } else {
            const trail = res.match(/^([\d.,\s]+)(\D+)$/);
            if (trail) res = code + " " + trail[1].trim();
          }
        }
        return res;
      }

      escape(s) {
        if (s == null) return "";
        const div = document.createElement("div");
        div.textContent = String(s);
        return div.innerHTML;
      }

      subscribeVariantChange() {
        if (
          typeof subscribe !== "function" ||
          typeof PUB_SUB_EVENTS === "undefined"
        ) {
          return;
        }
        this.unsubscribe = subscribe(
          PUB_SUB_EVENTS.variantChange,
          ({ data }) => {
            if (!data || data.sectionId !== this.dataset.sectionId) return;
            const variant = data.variant;
            if (!variant) {
              this.hidden = true;
              this.clearSellingPlan();
              return;
            }
            this.variantId = variant.id;
            this.variantPrice = variant.price;
            this.variantComparePrice = variant.compare_at_price || 0;
            this.variantTitle = variant.title || "";
            this.selectedPlanId = null;
            this.render();
          },
        );
      }
    },
  );
}
